import React, { useEffect, useRef } from 'react';

interface TradingViewChartProps {
  symbol?: string;
  theme?: 'light' | 'dark';
}

declare global {
  interface Window {
    TradingView: any;
  }
}

const TradingViewChart: React.FC<TradingViewChartProps> = ({
  symbol = 'AAPL',
  theme = 'dark'
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);

  useEffect(() => {
    // Load TradingView script if not already loaded
    const scriptId = 'tradingview-widget-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => initWidget();
      document.head.appendChild(script);
    } else if (window.TradingView) {
      initWidget();
    }

    function initWidget() {
      if (containerRef.current && window.TradingView) {
        // Clear previous widget
        if (widgetRef.current) {
          widgetRef.current = null;
        }
        containerRef.current.innerHTML = '';

        // Create new widget
        widgetRef.current = new window.TradingView.widget({
          autosize: true,
          symbol: symbol,
          interval: 'D',
          timezone: 'Etc/UTC',
          theme: theme,
          style: '1',
          locale: 'en',
          toolbar_bg: theme === 'dark' ? '#0a0a0a' : '#f1f3f6',
          enable_publishing: false,
          hide_top_toolbar: false,
          hide_legend: false,
          save_image: false,
          container_id: containerRef.current.id,
          studies: [
            'MASimple@tv-basicstudies'
          ],
          disabled_features: [
            'use_localstorage_for_settings',
            'header_compare',
            'display_market_status'
          ],
          enabled_features: [
            'study_templates'
          ],
          loading_screen: {
            backgroundColor: theme === 'dark' ? '#0a0a0a' : '#ffffff',
            foregroundColor: theme === 'dark' ? '#7dd3c0' : '#2962FF'
          },
          overrides: {
            'mainSeriesProperties.candleStyle.upColor': '#7dd3c0',
            'mainSeriesProperties.candleStyle.downColor': '#ff6b6b',
            'mainSeriesProperties.candleStyle.borderUpColor': '#7dd3c0',
            'mainSeriesProperties.candleStyle.borderDownColor': '#ff6b6b',
            'mainSeriesProperties.candleStyle.wickUpColor': '#7dd3c0',
            'mainSeriesProperties.candleStyle.wickDownColor': '#ff6b6b',
            'paneProperties.background': theme === 'dark' ? '#0a0a0a' : '#ffffff',
            'paneProperties.backgroundType': 'solid',
          }
        });
      }
    }

    return () => {
      if (widgetRef.current) {
        widgetRef.current = null;
      }
    };
  }, [symbol, theme]);

  return (
    <div
      id={`tradingview_${symbol}`}
      ref={containerRef}
      style={{
        height: '500px',
        width: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: theme === 'dark' ? '#0a0a0a' : '#ffffff'
      }}
    />
  );
};

export default TradingViewChart;
